import moment from "moment";
import {
  fetchCreateNewSegment,
  fetchEquipment,
  fetchMoveAllowedZones,
  fetchMoveExistSegment,
  fetchGetOrderItem,
  fetchProductPlan,
} from "../../../api";
import { useTimeline } from "../../../context/TimelineProvider";
import { notifyError } from "../../../../../utils/toasts";

export const useTimelineMethods = (divisionId) => {
  const {
    segments,
    addSegments,
    fillBlockedSegment,
    addGroups,
    groups,
    rollbackAllSegments,
    resetSegments,
    deleteSelectedSegment,
    selectSegment,
    selectedSegment,
    removeSegmentTooltip,
    addSegmentTooltip,
    addTooltipPosition,
    selectOrderItemId,
    selectedOrderItemId,
    addOrderFormData,
    addOrderFormColor,
    visibleTimeStart,
    visibleTimeEnd,
    addVisibleTimeStart,
    addVisibleTimeEnd,
    updateHash,
    updateOrders,
    resetFreeZoneSegments,
  } = useTimeline();

  // TODO: переиспользовать getProductData из этого хука во всех компонентах таймлайна
  async function getProductData() {
    try {
      const response = await fetchProductPlan({ divisionId }); // Асинхронный вызов
      const newHash = response?.data?.hash;
      if (newHash) {
        updateHash(response.data.hash); // Обновление хэша

        // Перезагружаем данные
        if (response.status === 200) {
          const newEquipment = await fetchEquipment({ divisionId });

          const newSegments = response.data.segments;

          resetTimeline();

          addGroups(
            newEquipment.data.map((eqItem) => ({
              id: eqItem.equipment_id,
              title: eqItem.name,
              height: 50,
              status: eqItem.status,
            }))
          );

          addSegments(
            newSegments.map((plan) => ({
              id: plan.segment_id,
              group: plan.equipment_id,
              title: plan.operation_name,
              start_time: moment(plan.start_time),
              end_time: moment(plan.end_time),
              order: plan.order_name,
              orderItemId: plan.order_item_id,
              quantity: plan.operation_quantity,
              style: {
                background: plan.order_color ?? "green",
                minHeight: "50px",
              },
            }))
          );
        }
      }
    } catch (error) {
      console.error("Error fetching product plan:", error);
    }
  }

  const defaultTimeStart = moment().startOf("day").toDate();
  const defaultTimeEnd = moment().startOf("day").add(1, "day").toDate();

  const defaultTimeRange = visibleTimeEnd - visibleTimeStart;

  const hasOverlap = (start, end, groupId, items, currentItemId) => {
    const startMoment = moment(start);
    const endMoment = moment(end);

    return items.some((item) => {
      const itemStart = moment(item.start_time);
      const itemEnd = moment(item.end_time);

      return (
        item.group === groupId &&
        item.id !== currentItemId && // Исключаем сам элемент
        (startMoment.isBetween(itemStart, itemEnd, null, "[)") || // Начало в пределах другого сегмента
          endMoment.isBetween(itemStart, itemEnd, null, "(]") || // Конец в пределах другого сегмента
          (startMoment.isSameOrBefore(itemStart) &&
            endMoment.isSameOrAfter(itemEnd))) // Полностью перекрывает другой сегмент
      );
    });
  };

  const canMoveItem = (itemId, group, time) => {
    const draggedItem = segments.find((item) => item.id === itemId);

    if (!draggedItem) return false;

    const now = moment().valueOf();
    const newStartTime = time;
    const newEndTime = time + (draggedItem.end_time - draggedItem.start_time);

    // Проверяем, не уходит ли сегмент в прошлое
    const isPast = newStartTime < now;

    return (
      !isPast && !hasOverlap(newStartTime, newEndTime, group, segments, itemId)
    );
  };

  const handleCheckAllowedZones = async (itemId, e) => {
    e.stopPropagation();

    const clickedItem = segments.find((item) => item.id === itemId);

    const time_now = moment().format("YYYY-MM-DD HH:mm:ss");

    if (!clickedItem) return;

    const response = await fetchMoveAllowedZones({
      segment_id: itemId,
      time_now,
    });

    if (!response || !response.data) {
      return;
    }

    const newLockedZones = [];

    groups.forEach((group) => {
      const equipmentData = response.data.find(
        (data) => data.equipment_id === group.id
      );

      if (!equipmentData) {
        // Если группа не найдена в ответе, полностью блокируем её
        const fullBlockExists = segments.some(
          (item) => item.id === `block-full-${group.id}`
        );

        if (!fullBlockExists) {
          newLockedZones.push({
            id: `block-full-${group.id}`,
            group: group.id,
            title: "Группа заблокирована",
            start_time: 0,
            end_time: moment(
              "2270-01-01 00:00:00",
              "YYYY-MM-DD HH:mm:ss"
            ).valueOf(),
            canMove: false,
            canSelect: false,
            style: {
              background: "rgba(216, 208, 208, 0.5)", // Полупрозрачный красный
              minHeight: "50px",
            },
          });
        }
        return;
      }

      const { restricted_zones } = equipmentData;

      // Обрабатываем `restricted_zones`
      restricted_zones.forEach((zone, index) => {
        const startTime = moment(
          zone.start_time,
          "YYYY-MM-DD HH:mm:ss"
        ).valueOf();
        const endTime = moment(zone.end_time, "YYYY-MM-DD HH:mm:ss").valueOf();

        const restrictedZoneExists = segments.some(
          (item) => item.id === `restricted-${group.id}-${index}`
        );

        if (!restrictedZoneExists) {
          newLockedZones.push({
            id: `restricted-${group.id}-${index}`,
            group: group.id,
            title: "Недоступная зона",
            start_time: startTime,
            end_time: endTime,
            canMove: false,
            canSelect: false,
            style: {
              background: "rgba(216, 208, 208, 0.5)", // Полупрозрачный красный
              minHeight: "50px",
            },
          });
        }
      });
    });

    addSegments(newLockedZones, divisionId);

    addSegmentTooltip({
      order: clickedItem.order || "Не указано",
      operation: clickedItem.title || "Не указано",
      quantity: clickedItem.quantity || "Не указано",
      start: moment(clickedItem.start_time).format("YYYY.MM.DD HH:mm:ss"),
      end: moment(clickedItem.end_time).format("YYYY.MM.DD HH:mm:ss"),
      duration: moment.duration(
        moment(clickedItem.end_time).diff(clickedItem.start_time)
      ),
    });

    addTooltipPosition({
      top: e.clientY - 170,
      left: e.clientX - 150,
    });
  };

  const resetTimeline = () => {
    resetSegments();
    removeSegmentTooltip();
  };

  const handleContextMenu = (e) => {
    e.preventDefault();

    getProductData();
  };

  const handleItemSelect = (itemId, e) => {
    const selectedItem = segments.find((item) => item.id === itemId);

    if (!selectedItem || selectedItem.canSelect === false) {
      alert("Выбранная зона недоступна.");
      return;
    }

    let background = selectedItem.style.background;

    if (itemId.startsWith("free-zone-")) {
      handleTemoraryClick(selectedItem);
      return;
    }

    selectSegment(itemId);

    handleCheckAllowedZones(itemId, e);
    selectOrderItemId(selectedItem.orderItemId);

    return background;
  };

  const handleDeleteSegment = async (divisionId) => {
    if (!selectedSegment) {
      alert("Выберите сегмент для удаления.");
      return;
    }

    await deleteSelectedSegment();

    const response = await fetchGetOrderItem(selectedOrderItemId);
    updateOrders(divisionId);

    addOrderFormData(response?.data);
    addOrderFormColor(response?.data.order_color || "#FFFFFF");

    getProductData();
  };

  const handleTemoraryClick = (selectedTemporarySegment) => {
    resetFreeZoneSegments();
    fetchCreateNewSegment({
      divisionId,
      operation_quantity: selectedTemporarySegment.operation_quantity,
      parallel_quantity: selectedTemporarySegment.parallel_quantity,
      start_time: moment(selectedTemporarySegment.operation_start_time).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
      duration: selectedTemporarySegment.operation_duration,
      order_item_id: selectedTemporarySegment.order_item_id,
      operation_id: selectedTemporarySegment.operation_id,
      group_id: selectedTemporarySegment.group_id,
      equipment_id: selectedTemporarySegment.equipment_id,
    }).then((res) => {
      fetchGetOrderItem(selectedOrderItemId).then((response) => {
        addOrderFormData(response?.data);
        addOrderFormColor(response?.data.order_color || "#FFFFFF");
      });

      getProductData();
    });
  };

  const handleItemMove = async (itemId, dragTime, newGroupOrder) => {
    const newGroupId = groups[newGroupOrder]?.id;

    const draggedItem = segments.find((item) => item.id === itemId);

    if (!draggedItem) return;

    const newStartTime = moment(dragTime).valueOf();
    const newEndTime = moment(
      dragTime + (draggedItem.end_time - draggedItem.start_time)
    ).valueOf();

    const now = moment().valueOf();
    const time_now = moment().format("YYYY-MM-DD HH:mm:ss");

    // Проверка пересечений и перемещение в прошлое
    if (newStartTime < now) {
      alert(
        "Перемещение невозможно: сегмент не может быть размещён в прошлом."
      );
      return;
    }

    if (hasOverlap(newStartTime, newEndTime, newGroupId, segments, itemId)) {
      alert("Перемещение невозможно: выбранное время уже занято.");
      return;
    }

    try {
      // Запрос доступных зон перемещения
      const response = await fetchMoveAllowedZones({
        segment_id: itemId,
        time_now,
      });

      if (!response || !response.data) {
        alert("Произошла ошибка при получении зон перемещения.");
        return;
      }

      const allowedEquipment = response.data.map((data) => data.equipment_id);

      // Проверяем, доступна ли новая группа для сегмента
      if (!allowedEquipment.includes(newGroupId)) {
        alert(
          "Перемещение невозможно: оборудование недоступно для этого сегмента."
        );
        return;
      }

      // Проверка на доступные временные зоны
      const equipmentData = response.data.find(
        (data) => data.equipment_id === newGroupId
      );

      if (!equipmentData) {
        alert("Перемещение невозможно: данные для оборудования отсутствуют.");
        return;
      }

      const { allowed_zones, restricted_zones } = equipmentData;

      // Проверяем, входит ли новая зона в разрешенные
      const isInAllowedZone = allowed_zones.some((zone) => {
        const allowedStartTime = moment(
          zone.start_time,
          "YYYY-MM-DD HH:mm:ss"
        ).valueOf();
        const allowedEndTime = moment(
          zone.end_time,
          "YYYY-MM-DD HH:mm:ss"
        ).valueOf();

        return newStartTime >= allowedStartTime && newEndTime <= allowedEndTime;
      });

      if (!isInAllowedZone) {
        alert(
          "Перемещение невозможно: выбранный временной интервал недоступен для оборудования."
        );
        return;
      }

      // Проверяем, не попадает ли новая зона в запрещенные
      const isInRestrictedZone = restricted_zones.some((zone) => {
        const restrictedStartTime = moment(
          zone.start_time,
          "YYYY-MM-DD HH:mm:ss"
        ).valueOf();
        const restrictedEndTime = moment(
          zone.end_time,
          "YYYY-MM-DD HH:mm:ss"
        ).valueOf();

        return (
          newStartTime < restrictedEndTime && newEndTime > restrictedStartTime
        );
      });

      if (isInRestrictedZone) {
        alert(
          "Перемещение невозможно: выбранный временной интервал пересекается с запрещённой зоной."
        );
        return;
      }

      // Обновляем данные сегмента
      const updatedSegment = {
        ...draggedItem,
        group: newGroupId,
        start_time: newStartTime,
        end_time: newEndTime,
      };

      fillBlockedSegment(updatedSegment);

      // Сохранение изменений на сервере
      await fetchMoveExistSegment({
        id: itemId,
        new_start_time: moment(dragTime).format("YYYY-MM-DD HH:mm:ss"),
        equipment_id: newGroupId,
      });
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
      alert("Произошла ошибка. Перемещение не выполнено.");
      rollbackAllSegments(); // Откат изменений
    }
  };

  // TODO: Оптимизировать. Возможно повесить debounce
  const handleWheelEvent = (e) => {
    if (e.ctrlKey || e.altKey) {
      e.preventDefault();
      const zoomSpeed = 0.1; // Чем меньше значение, тем плавнее
      const direction = e.deltaY > 0 ? 1 : -1;
      const zoomDelta =
        (visibleTimeEnd - visibleTimeStart) * zoomSpeed * direction;

      addVisibleTimeStart((prev) => prev - zoomDelta / 2);
      addVisibleTimeEnd((prev) => prev + zoomDelta / 2);
    }
  };

  const handleTimeChange = (_start, _end, updateScrollCanvas) => {
    addVisibleTimeStart(_start);
    addVisibleTimeEnd(_end);
    updateScrollCanvas(_start, _end);
  };

  return {
    handleCheckAllowedZones,
    handleContextMenu,
    handleDeleteSegment,
    handleWheelEvent,
    handleItemMove,
    handleItemSelect,
    canMoveItem,
    defaultTimeStart,
    defaultTimeEnd,
    defaultTimeRange,
    handleTimeChange,
    resetTimeline,
    getProductData,
  };
};
